/* eslint-disable max-lines */
import { getFeature, isFeature } from 'Cloud/Application/FeaturesEs6';
import { ReactComponent as BenefitLabel } from 'img/quota-landing/aiTariff/benefitLabel.svg';
import { ReactComponent as Icon20Stars } from 'img/quota-landing/aiTariff/stars_20.svg';
import { DebugLogger } from 'lib/debugLogger';
import React, { type ReactElement } from 'react';
import { IS_BIZ_USER, IS_CORP_USER } from 'reactApp/appHelpers/configHelpers';
import {
    aiTariffAbEnabled,
    aiTariffAbVariantB,
    aiTariffAbVariantC1,
    aiTariffAbVariantC2,
    aiTariffAbVariantD,
} from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { businessTariffsPromoQuota } from 'reactApp/appHelpers/featuresHelpers/features/businessTariffsPromoQuota';
import { isFeatureNeurofilterPromo } from 'reactApp/appHelpers/featuresHelpers/features/neurofilter';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { features } from 'reactApp/mail-features';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';

const queryParams = getQueryParams();

export const multiTariffShow = isFeature('multi-tariff-show');

export const multiTariffKeepOrderAsInFeature = isFeature('multi-tariff-keep-list-order');

function initTabFallback(initTab) {
    if (initTab === ETabsName.for_work && !businessTariffsPromoQuota.enabled) {
        return ETabsName.advanced;
    }
    return initTab;
}

export const multiTariffInitTab = initTabFallback(queryParams?.initTab || getFeature('multi-tariff-init-tab')) || ETabsName.basic;

export const multiTariffInitPeriodTab: ETabsName | undefined = getFeature('multi-tariff-init-period-tab');

export const familyByOptionsSwitcherState = isFeature('family-by-options-switcher-state');

export const familyByOptionsFeature = isFeature('family-by-options-feature');
export const familyByOptionsEnterTooltipFeature = isFeature('family-by-options-enter-tooltip');

export const multiBasicWithoutBaz = isFeature('multi-basic-without-baz');

type TariffList = {
    year: string[];
    month: string[];
};

export const multiTariffBasicTariffList: TariffList = getFeature('multi-tariff-basic-tariff-list') || {
    year: [
        'W2TB_1y_blackfriday_2023_discount_single_quota_cloud',
        'W1TB_1y_blackfriday_2023_discount_single_quota_cloud',
        'W512GB_1y_blackfriday_2023_discount_single_quota_cloud',
        'W256GB_1y_blackfriday_2023_discount_single_quota_cloud',
    ],
    month: [
        'W2T1m_single_quota_2022_cloud',
        'W1T1m_single_quota_2022_cloud',
        'W512G1m_single_quota_2022_cloud',
        'W256G1m_single_quota_2022_cloud',
    ],
};

export const multiTariffAdvancedTariffList: TariffList = getFeature('multi-tariff-advanced-tariff-list') || {
    year: [
        'W256GB_1y_landing_segmentation_discount',
        'W512GB_1y_landing_segmentation_discount',
        'W1TB_1y_landing_segmentation_discount',
        'W2TB_1y_landing_segmentation_discount',
    ],
    month: [
        'W256GB_1m_landing_segmentation_discount',
        'W512GB_1m_landing_segmentation_discount',
        'W1TB_1m_landing_segmentation_discount',
        'W2TB_1m_landing_segmentation_discount',
    ],
};

export const multiTariffFamilyTariffForAnonym = getFeature('multi-tariff-family-tariff-anonym') || 'W1TB_family_features_a';

export const multiTariffBasicTariffListForAnonym: TariffList = getFeature('multi-tariff-basic-tariff-list-anonym') || {
    year: [
        'W256GB_1y_minimal_line_with_ads_discount',
        'W512GB_1y_minimal_line_with_ads_discount',
        'W1TB_1y_minimal_line_with_ads_discount',
        'W2TB_1y_minimal_line_with_ads_discount',
    ],
    month: [
        'W256G1m_single_quota_2022_cloud',
        'W512GB_1m_minimal_line_with_ads',
        'W1TB_1m_minimal_line_with_ads',
        'W2TB_1m_minimal_line_with_ads',
    ],
};

export const multiTariffAdvancedTariffListForAnonym: TariffList = getFeature('multi-tariff-advanced-tariff-list-anonym') || {
    year: [
        'W256GB_1y_landing_segmentation_discount',
        'W512GB_1y_landing_segmentation_discount',
        'W1TB_1y_landing_segmentation_discount',
        'W2TB_1y_landing_segmentation_discount',
    ],
    month: [
        'W256GB_1m_landing_segmentation_discount',
        'W512GB_1m_landing_segmentation_discount',
        'W1TB_1m_landing_segmentation_discount',
        'W2TB_1m_landing_segmentation_discount',
    ],
};

export const multiTariffFamilyTariff = getFeature('multi-tariff-family-tariff') || 'W1TB_family_features_a';

export const enum BenefitsFeaturesIds {
    docs = 'docs',
    autosave = 'autosave',
    size = 'size',
    infinity = 'infinity',
    support = 'support',
    ad = 'ad',
    upload = 'upload',
    autodelete = 'autodelete',
    themedPublic = 'themed-public',
    password = 'password',
    family = 'family',
    familyFolders = 'family-folders',
    familyAlbums = 'family-albums',
    familyCalendar = 'family-calendar',
    familyDocs = 'family-docs',
    benefits = 'benefits',
    createVigbo = 'create-vigbo',
    funcVigbo = 'func-vigbo',
    neuroassistant = 'neuroassistant',
    virus = 'virus',
    duplicate = 'duplicate',
    mailSize = 'mail-size',
    send = 'send',
    cancel = 'cancel',
    notification = 'notification',
    widgets = 'widgets',
    swipes = 'swipes',
    faces = 'faces',
    ailetterstyle = 'ailetterstyle',
    aisummary = 'aisummary',
    antispam = 'antispam',
    beautiful = 'beautiful',
    aiimprove = 'aiimprove',
    aigenerate = 'aigenerate',
    airmvback = 'airmvback',
}

export interface BenefitData {
    id: BenefitsFeaturesIds;
    name: string;
    free: boolean;
    paid: boolean;
    advanced: boolean;
    family?: boolean;
    isNew?: boolean;
    icon?: ReactElement;
    // Для подстветки фона нескольких фич одним блоком
    firstSelection?: boolean;
    lastSelection?: boolean;
    isSelection?: boolean;
    isNeuro?: boolean;
    vigbo?: boolean;
    forWork?: boolean;
}

export const multiTariffCloudBenefits: BenefitData[] = [
    {
        id: BenefitsFeaturesIds.docs,
        name: 'Редактирование документов',
        free: true,
        paid: true,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.autosave,
        name: 'Автосохранение файлов с телефона в Облако',
        free: true,
        paid: true,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.size,
        name: 'Дополнительное место для файлов',
        free: false,
        paid: true,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.infinity,
        name: 'Безлимитная автозагрузка фото и видео с телефона',
        free: false,
        paid: true,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.support,
        name: 'Приоритетная поддержка',
        free: false,
        paid: true,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.ad,
        // eslint-disable-next-line sonarjs/no-duplicate-string
        name: 'Без рекламы',
        free: false,
        paid: false,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.upload,
        name: 'Загрузка файлов до 100 ГБ в Облако',
        free: false,
        paid: false,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.autodelete,
        name: 'Таймер для автоудаления отправленных папок',
        free: false,
        paid: false,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.themedPublic,
        name: 'Выбор оформления для совместных папок',
        free: false,
        paid: false,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.password,
        name: 'Пароль для совместных папок в веб-версии',
        free: false,
        paid: false,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.family,
        name: 'Можно делиться памятью ещё с 3 участниками',
        free: false,
        paid: false,
        advanced: !familyByOptionsFeature && !businessTariffsPromoQuota.enabled,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.familyFolders,
        name: 'Семейные папки с общим доступом',
        free: false,
        paid: false,
        advanced: false,
        family: true,
        isNew: true,
    },
    {
        id: BenefitsFeaturesIds.familyAlbums,
        name: 'Семейные альбомы с общим доступом',
        free: false,
        paid: false,
        advanced: false,
        family: true,
        isNew: true,
    },
    {
        id: BenefitsFeaturesIds.familyCalendar,
        name: 'Семейный календарь',
        free: false,
        paid: false,
        advanced: false,
        family: true,
        isNew: true,
    },
    {
        id: BenefitsFeaturesIds.familyDocs,
        name: 'Распознавание документов семьи',
        free: false,
        paid: false,
        advanced: false,
        family: true,
        isNew: true,
    },
    {
        id: BenefitsFeaturesIds.benefits,
        name: 'Безлимит и отключение рекламы у всех в подписке',
        free: false,
        paid: false,
        advanced: false,
        family: true,
        isNew: true,
    },
    {
        id: BenefitsFeaturesIds.createVigbo,
        name: 'Создание галерей Vigbo',
        free: false,
        paid: false,
        advanced: false,
        family: false,
        isNew: true,
    },
    {
        id: BenefitsFeaturesIds.funcVigbo,
        name: 'Функция ретуши в Vigbo',
        free: false,
        paid: false,
        advanced: false,
        family: false,
        isNew: true,
    },
];

export const multiTariffMailBenefits: BenefitData[] = [
    {
        id: BenefitsFeaturesIds.virus,
        name: 'Проверка вложений антивирусом',
        free: true,
        paid: true,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.mailSize,
        name: 'Дополнительное место для писем',
        free: false,
        paid: true,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.ad,
        name: 'Без рекламы',
        free: false,
        paid: false,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.send,
        name: 'Отправка вложений до 100 ГБ в письмах',
        free: false,
        paid: false,
        advanced: true,
        family: true,
    },
    {
        id: BenefitsFeaturesIds.widgets,
        name: 'Полезные виджеты Календаря и Задач всегда на виду',
        free: false,
        paid: false,
        advanced: true,
        family: true,
        isNew: false,
    },
    {
        id: BenefitsFeaturesIds.cancel,
        name: 'Отмена отправки писем в течение 20 секунд',
        free: false,
        paid: false,
        advanced: !businessTariffsPromoQuota.enabled,
        family: !businessTariffsPromoQuota.enabled,
    },
    {
        id: BenefitsFeaturesIds.notification,
        name: 'Уведомления о прочтении ваших писем',
        free: false,
        paid: false,
        advanced: !businessTariffsPromoQuota.enabled,
        family: !businessTariffsPromoQuota.enabled,
    },
];

if (isFeatureNeurofilterPromo && !aiTariffAbEnabled) {
    multiTariffMailBenefits.push({
        id: BenefitsFeaturesIds.neuroassistant,
        name: 'Нейропомощник в веб-версии Почты',
        free: false,
        paid: false,
        advanced: true,
        family: true,
        isNew: true,
    });
}

const familyByOptionsMonthTariffs = getFeature('family-by-options-month-tariffs') || [
    'W256GB_1m_family_features_discount_3m',
    'W512GB_1m_family_features_discount_3m',
    'W1TB_1m_family_features_a',
    'W2TB_1m_family_features_discount_3m',
];

const familyByOptionsYearTariffs = getFeature('family-by-options-year-tariffs') || [
    'W256GB_1y_family_features_discount',
    'W512GB_1y_family_features_discount',
    'W1TB_1y_family_features_a',
    'W2TB_1y_family_features_discount',
];

export const familyByOptionsTariffs = {
    year: familyByOptionsYearTariffs,
    month: familyByOptionsMonthTariffs,
};

export const familyUpsaleEnable = isFeature('family-upsale-enable');
export const familyUpsaleTariffs = {
    year: getFeature('family-upsale-year-tariffs') || familyByOptionsYearTariffs,
    month: getFeature('family-upsale-month-tariffs') || familyByOptionsMonthTariffs,
};

export const familySpaceUpsaleEnable = isFeature('family-space-upsale-enable');

export const familyAndBusinessUpsaleEnable = isFeature('family-and-business-upsale-enable');

export const vigboTariffListEnabled = isFeature('vigbo-tariff-list-enabled');
export const vigboTariffTooltipEnabled = isFeature('vigbo-tariff-tooltip-enable');

export const multiTariffVigboTariffList: TariffList = getFeature('multi-tariff-advanced-tariff-list') || {
    year: ['W256GB_1y_vigbo_discount', 'W1TB_1y_vigbo_discount', 'W2TB_1y_vigbo_discount', 'W4TB_1y_vigbo_discount'],
    month: ['W256GB_1m_vigbo_discount', 'W1TB_1m_vigbo_discount', 'W2TB_1m_vigbo_discount', 'W4TB_1m_vigbo_discount'],
};

const businessPatch: Partial<BenefitData> = businessTariffsPromoQuota.enabled
    ? {
          icon: undefined,
          isNew: true,
      }
    : {};

export const aiTariffAbVariantBCloudBenefits: BenefitData[] = [
    {
        id: BenefitsFeaturesIds.duplicate,
        name: 'Удаление дубликатов',
        free: false,
        paid: false,
        family: businessTariffsPromoQuota.enabled,
        advanced: !aiTariffAbVariantB,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        firstSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
        // eslint-disable-next-line max-lines
    },
    {
        id: BenefitsFeaturesIds.swipes,
        name: 'Разбор фото свайпами',
        free: false,
        // eslint-disable-next-line max-lines
        paid: false,
        family: businessTariffsPromoQuota.enabled,
        advanced: !aiTariffAbVariantB,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        isSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
    },
    {
        id: BenefitsFeaturesIds.faces,
        name: 'Определение лиц на фото',
        free: false,
        paid: false,
        family: businessTariffsPromoQuota.enabled,
        advanced: !aiTariffAbVariantB,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        lastSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
    },
    {
        id: BenefitsFeaturesIds.aiimprove,
        name: 'Улучшение фото в веб-версии',
        free: false,
        paid: false,
        family: businessTariffsPromoQuota.enabled,
        advanced: !aiTariffAbVariantB,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        lastSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
    },
    {
        id: BenefitsFeaturesIds.aigenerate,
        name: 'Создание изображений в мобильной версии',
        free: false,
        paid: false,
        family: businessTariffsPromoQuota.enabled,
        advanced: !aiTariffAbVariantB,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        lastSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
    },
];

export const aiTariffRmvBackCloudBenefits: BenefitData[] = [
    {
        id: BenefitsFeaturesIds.airmvback,
        name: 'Удаление фона с фото',
        free: false,
        paid: false,
        family: businessTariffsPromoQuota.enabled,
        advanced: true,
        isNeuro: true,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        lastSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...businessPatch,
    },
];

export const aiTariffAbVariantBMailBenefits: BenefitData[] = [
    {
        id: BenefitsFeaturesIds.ailetterstyle,
        name: 'Генерация и изменение стиля писем',
        free: false,
        paid: false,
        family: false,
        advanced: !aiTariffAbVariantB && !businessTariffsPromoQuota.enabled,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        firstSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
    },
    {
        id: BenefitsFeaturesIds.aisummary,
        name: 'Краткий пересказ писем',
        free: false,
        paid: false,
        family: false,
        advanced: !aiTariffAbVariantB && !businessTariffsPromoQuota.enabled,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        isSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
    },
    {
        id: BenefitsFeaturesIds.neuroassistant,
        name: 'Нейропомощник',
        free: false,
        paid: false,
        family: false,
        advanced: !aiTariffAbVariantB && !businessTariffsPromoQuota.enabled,
        icon: aiTariffAbVariantB ? <Icon20Stars /> : <BenefitLabel />,
        isSelection: aiTariffAbVariantC1 || aiTariffAbVariantC2,
        ...(aiTariffAbVariantD ? {} : businessPatch),
    },
];

export const multiTariffFamilyTariffListEnable: boolean = isFeature('multi-tariff-family-tariff-list-enable');
export const multiTariffFamilyTariffList: TariffList = getFeature('multi-tariff-family-tariff-list') || {
    year: [
        'W256GB_1y_family_features_discount',
        'W512GB_1y_family_features_discount',
        'W1TB_1y_family_features_a',
        'W2TB_1y_family_features_discount',
    ],
    month: [
        'W256GB_1m_family_features_discount_3m',
        'W512GB_1m_family_features_discount_3m',
        'W1TB_1m_family_features_a',
        'W2TB_1m_family_features_discount_3m',
    ],
};

/** CLOUD-4744: Фича для редизайна семейных карточек под выделение места в квоте каждому члену семьи*/
export const multiTariffSpaceToEachFamilyMember: boolean =
    isFeature('multi-tariff-space-to-each-family-member') && !IS_BIZ_USER && !IS_CORP_USER;

/** tempexp_19921-next-line Фича выключения Таба (минимальные) */
export const multiTariffDisableBasic: boolean = isFeature('multi-tariff-disable-basic');

export const multiTariffSendTariffAnalytics: boolean = isFeature('multi-tariff-send-tariff-analytics');

/** CLOUD-20115: Фича для скрытия переключателя смены периода тарифа на десктопе*/
export const multiTariffHidePeriodSwitchDesktop: boolean = isFeature('multi-tariff-hide-period-switch-desktop');

export const enum multiTariffTabPersonalizationVariants {
    year = 'year',
    month = 'month',
    onlyMonth = 'onlyMonth',
}

const multiTariffTabPersonalization: multiTariffTabPersonalizationVariants | undefined = getFeature('multi-tariff-tab-personalization');
export const multiTariffTapPersonalizationInitTabs: ETabsName | undefined =
    multiTariffTabPersonalization &&
    (multiTariffTabPersonalization === multiTariffTabPersonalizationVariants.year ? ETabsName.year : ETabsName.month);
export const isMultiTariffTabPersonalizationOnlyMonth: boolean =
    multiTariffTabPersonalization === multiTariffTabPersonalizationVariants.onlyMonth;

/**
 * CLOUDWEB-20288: Технический параметр – выпилить после включения на 100
 */
export const multiTariffHashTariffAnalytics: boolean = features.has('multi-tariff-hash-tariff-analytics');

export const multiTariffDebug = features.has('multi-tariff-debug');

export const multiTariffDebugLogger = new DebugLogger({ DEBUG_SCOPE: 'MultiTariff', DEBUG: multiTariffDebug });
